import { definedMap, withDefault } from "collection-utils";
import {
    Corners,
    ImageAspectRatio,
    OverlayTextAlignment,
    TextPosition,
    TilesPadding,
} from "common/components/image-types";
import type { DropImageHandler } from "common/components/portable-renderers";
import { defined } from "common/Support";
import { TextSize, TextStyle } from "component-utils/text-types";
import type { OnClickHandler } from "hooks/use-on-click-from-action";
import * as React from "react";
import { scaleImageWidthToHeight } from "../../../lib/image-utils";
import OverlayContainerView from "../overlay-container/overlay-container-view";
import type { ShadowPosition } from "./tile-item-view-style";
import {
    InitialsText,
    TileImageWrapper,
    TileItemImageStyle,
    TileItemInitials,
    TileItemViewStyle,
} from "./tile-item-view-style";

export interface TileItemViewProps {
    // required props
    readonly itemID: string | undefined;
    readonly url: string;
    readonly size: ImageAspectRatio;
    readonly columnWidth: number;

    // optional props
    readonly className?: string;
    readonly title?: string;
    readonly subtitle?: string;
    readonly fallbackInitials?: string;
    readonly allowWrapping?: boolean;
    readonly cornersStyle?: Corners;
    readonly textPosition?: TextPosition;
    readonly overlayTextAlignment?: OverlayTextAlignment;
    readonly textSize?: TextSize;
    readonly textStyle?: TextStyle;
    readonly padding?: TilesPadding;
    readonly topLeftOverlay?: React.ReactNode;
    readonly topRightOverlay?: React.ReactNode;
    readonly bottomLeftOverlay?: React.ReactNode;
    readonly bottomRightOverlay?: React.ReactNode;

    // action props
    readonly onClick?: OnClickHandler;
    readonly onDropImage?: DropImageHandler;
}

export default class TileItemView extends React.PureComponent<TileItemViewProps> {
    private ImageWithFallback = (props: {
        url: string;
        size: ImageAspectRatio;
        fallback: React.ReactNode;
        columnWidth: number;
        hasCorners: boolean;
    }) => {
        const { columnWidth, size, fallback, hasCorners, url } = props;

        const width = columnWidth;
        const height = scaleImageWidthToHeight(width, size);
        const minDimension = Math.min(width, height);

        const inner = (
            <TileItemImageStyle
                onDropImage={this.props.onDropImage}
                src={url}
                alternate={fallback}
                draggable={false}
                minDimension={minDimension}
            />
        );
        return (
            <TileImageWrapper
                hasCorners={hasCorners}
                height={height}
                width={width}
                isCircle={size === ImageAspectRatio.Circle}>
                {inner}
            </TileImageWrapper>
        );
    };

    private onClick = () => defined(this.props.onClick)(false);

    public render(): React.ReactNode {
        const textSize = withDefault(this.props.textSize, TextSize.Medium);
        const textPosition = withDefault(this.props.textPosition, TextPosition.Below);
        const alignment = withDefault(this.props.overlayTextAlignment, OverlayTextAlignment.Center);
        const textStyle = withDefault(this.props.textStyle, TextStyle.Normal);
        const allowWrapping = withDefault(this.props.allowWrapping, true) && textPosition === TextPosition.Below;
        const padding = withDefault(this.props.padding, TilesPadding.Loose);

        const {
            itemID,
            title,
            subtitle,
            url,
            size,
            fallbackInitials,
            onClick,
            columnWidth,
            cornersStyle,
            topLeftOverlay,
            topRightOverlay,
            bottomLeftOverlay,
            bottomRightOverlay,
            className,
        } = this.props;

        const corners = cornersStyle !== Corners.Square;

        const fallback = definedMap(fallbackInitials, f => (
            <TileItemInitials hasCorners={corners}>
                <InitialsText>{f}</InitialsText>
            </TileItemInitials>
        ));

        const hasTitle = title !== undefined;
        const hasSubtitle = subtitle !== undefined;

        const textContent =
            hasTitle || hasSubtitle ? (
                <div className="tile-text-container">
                    {hasTitle && (
                        <div className="tile-title" data-test="tile-item-title">
                            {title}
                        </div>
                    )}
                    {hasSubtitle && (
                        <div className="tile-subtitle" data-test="tile-item-title">
                            {subtitle}
                        </div>
                    )}
                </div>
            ) : undefined;

        let main: OverlayTextAlignment | undefined;
        function generateCornerContent(corner: OverlayTextAlignment, overlay: React.ReactNode) {
            if (
                (textContent === undefined || alignment !== corner || textPosition !== TextPosition.Overlay) &&
                overlay === undefined
            ) {
                return undefined;
            }

            const hasText = alignment === corner && textPosition === TextPosition.Overlay;
            if (hasText) {
                main = corner;
            }

            return (
                <>
                    {hasText && textContent}
                    {overlay}
                </>
            );
        }

        const topLeft = generateCornerContent(OverlayTextAlignment.TopLeft, topLeftOverlay);
        const topRight = generateCornerContent(OverlayTextAlignment.TopRight, topRightOverlay);
        const bottomLeft = generateCornerContent(OverlayTextAlignment.BottomLeft, bottomLeftOverlay);
        const bottomRight = generateCornerContent(OverlayTextAlignment.BottomRight, bottomRightOverlay);
        const center = generateCornerContent(OverlayTextAlignment.Center, undefined);

        const needBottom = center !== undefined || bottomLeft !== undefined || bottomRight !== undefined;
        const needTop =
            center !== undefined ||
            (textPosition === TextPosition.Overlay &&
                (alignment === OverlayTextAlignment.TopLeft || alignment === OverlayTextAlignment.TopRight));

        let shadow: ShadowPosition = "none";
        if (needBottom && needTop) {
            shadow = "full";
        } else if (needTop) {
            shadow = "top";
        } else if (needBottom) {
            shadow = "bottom";
        }

        const tileClassName = itemID !== undefined ? `selected-item-list-item-${itemID}` : undefined;

        return (
            <TileItemViewStyle
                data-test="tile-item"
                className={className ? `${className} ` : undefined + `${tileClassName}`}
                textSize={textSize}
                textPosition={textPosition}
                textStyle={textStyle}
                alignment={alignment}
                hasCorners={corners}
                shadow={shadow}
                onClick={onClick === undefined ? undefined : this.onClick}
                padBottom={padding === TilesPadding.Tight}
                wrapping={allowWrapping}
                isCircle={size === ImageAspectRatio.Circle}>
                <div className="tile-inner">
                    <div className="tile-image-area">
                        <this.ImageWithFallback
                            url={url}
                            fallback={fallback}
                            columnWidth={columnWidth}
                            size={size}
                            hasCorners={corners}
                        />
                        <OverlayContainerView
                            className="tile-overlay"
                            mainCorner={main}
                            center={center}
                            topLeft={topLeft}
                            topRight={topRight}
                            bottomLeft={bottomLeft}
                            bottomRight={bottomRight}
                        />
                    </div>
                    {textPosition === TextPosition.Below && textContent}
                </div>
            </TileItemViewStyle>
        );
    }
}
